import { useLoadoutStore } from '../store/loadoutSlice';
import { useCharacterProgressionStore } from '../store/characterProgressionSlice';
import {
  PERSO_SLOT_COUNT,
  type TrinketId,
  type TrinketLevel,
} from '../types/trinket.types';
import type { SketchModelId } from '../types/sketch.types';
import type { SocketSlots } from '../types/modifier.types';
import { LEVEL_CAP } from '../data/progression.config';
import {
  TRINKET_MAJOR_REGISTRY,
  TRINKET_MINOR_REGISTRY,
} from '../data/trinkets.config';
import { SPELL_REGISTRY } from '../data/spells.config';
import { SKETCH_REGISTRY } from '../data/sketches.config';
import { MODIFIER_REGISTRY } from '../data/modifiers.config';
import { useSocketsStore } from '../store/socketsSlice';
import { getMasteryLevelLive } from './SpellMasteryService';

export interface LoadoutSlotSnapshot {
  spellId: string | null;
  minorId: TrinketId | null;
  minorLevel: TrinketLevel | null;
}

export interface PlayerLoadoutSnapshot {
  level: number;
  major: TrinketId | null;
  majorLevel: TrinketLevel | null;
  slots: LoadoutSlotSnapshot[];
  sketches: SketchModelId[];
  /** Socketed modifier ids per equipped minor (keyed by modelId). Carried for a
   *  remote player so the host rebuilds its spell instances from the right
   *  modifiers; the local player reads the live store instead. */
  sockets?: Record<string, SocketSlots>;
  /** Per-spell mastery levels for a remote player; the local player reads its
   *  live catalog store. */
  mastery?: Record<string, number>;
}

/** Read model for building a player — structurally identical to a captured
 *  snapshot, backed by the live stores (local) or a received snapshot (remote). */
export type LoadoutSource = PlayerLoadoutSnapshot;

export function captureLoadoutSnapshot(): PlayerLoadoutSnapshot {
  const loadout = useLoadoutStore.getState();
  const major = loadout.equipped.major;
  const slots: LoadoutSlotSnapshot[] = Array.from(
    { length: PERSO_SLOT_COUNT },
    (_, i) => {
      const s = loadout.equipped.slots[i];
      const minorId = s?.minorId ?? null;
      return {
        spellId: s?.spellId ?? null,
        minorId,
        minorLevel: minorId ? loadout.getMinorLevel(minorId) : null,
      };
    },
  );
  const sketches = Object.values(loadout.equipped.sketches).filter(
    (id): id is SketchModelId => id !== null,
  );
  const socketsState = useSocketsStore.getState().socketed;
  const sockets: Record<string, SocketSlots> = {};
  for (const s of slots) {
    if (s.minorId && socketsState[s.minorId]?.some((m) => m != null)) {
      sockets[s.minorId] = socketsState[s.minorId];
    }
  }
  const mastery: Record<string, number> = {};
  for (const s of slots) {
    if (s.spellId) {
      mastery[s.spellId] = getMasteryLevelLive(s.spellId);
    }
  }
  return {
    level: useCharacterProgressionStore.getState().level,
    major,
    majorLevel: loadout.getMajorLevel(major),
    slots,
    sketches,
    sockets,
    mastery,
  };
}

export function liveLoadoutSource(): LoadoutSource {
  return captureLoadoutSnapshot();
}

function clampLevel(level: number | null, maxLevel: number): TrinketLevel {
  const n = Number.isFinite(level as number) ? Math.floor(level as number) : 1;
  return Math.max(1, Math.min(n, maxLevel));
}

/**
 * Host-side defence: a client controls its own loadout snapshot, so the host
 * clamps levels to the legitimate maxima and drops ids absent from the static
 * registries before building the remote player. This is NOT unlock validation
 * (that needs the host's unlock state, deferred) — only bounding against a
 * forged/buggy snapshot corrupting host-authoritative stats.
 */
export function sanitizeLoadoutSnapshot(
  snap: PlayerLoadoutSnapshot,
): PlayerLoadoutSnapshot {
  const level = Number.isFinite(snap.level)
    ? Math.max(1, Math.min(Math.floor(snap.level), LEVEL_CAP))
    : 1;

  const majorDef = snap.major ? TRINKET_MAJOR_REGISTRY[snap.major] : undefined;
  const major = majorDef ? snap.major : null;
  const majorLevel = majorDef
    ? clampLevel(snap.majorLevel, majorDef.maxLevel)
    : null;

  const slots: LoadoutSlotSnapshot[] = Array.from(
    { length: PERSO_SLOT_COUNT },
    (_, i) => {
      const s = snap.slots?.[i];
      const spellId =
        s?.spellId && SPELL_REGISTRY[s.spellId] ? s.spellId : null;
      const minorDef = s?.minorId
        ? TRINKET_MINOR_REGISTRY[s.minorId]
        : undefined;
      const minorId = minorDef ? s.minorId! : null;
      const minorLevel = minorDef
        ? clampLevel(s!.minorLevel, minorDef.maxLevel)
        : null;
      return { spellId, minorId, minorLevel };
    },
  );

  const sketches = (snap.sketches ?? []).filter(
    (id) => SKETCH_REGISTRY[id] != null,
  );

  const validMinorIds = new Set(
    slots.map((s) => s.minorId).filter((id): id is TrinketId => id !== null),
  );
  const sockets: Record<string, SocketSlots> = {};
  for (const [minorId, list] of Object.entries(snap.sockets ?? {})) {
    if (!validMinorIds.has(minorId as TrinketId)) {
      continue;
    }
    const def = TRINKET_MINOR_REGISTRY[minorId as TrinketId];
    const cap = def?.socketSlots ?? 0;
    sockets[minorId] = (list ?? [])
      .map((m) => (m && MODIFIER_REGISTRY[m] ? m : null))
      .slice(0, cap);
  }
  const mastery: Record<string, number> = {};
  for (const [spellId, lvl] of Object.entries(snap.mastery ?? {})) {
    if (SPELL_REGISTRY[spellId]) {
      mastery[spellId] = Math.max(1, Math.min(5, Math.floor(lvl)));
    }
  }

  return { level, major, majorLevel, slots, sketches, sockets, mastery };
}
